import { useMemo } from 'react'
import type { FreeboxBox } from '@/lib/freebox'
import {
  portLabel,
  portTempTone,
  type MikrotikDevice,
  type MikrotikLatest,
  type MikrotikPort,
} from '@/lib/mikrotik'

const STROKE = {
  up: 'hsl(var(--primary))',
  warn: 'hsl(38 92% 50%)',
  crit: 'hsl(var(--destructive))',
  down: 'hsl(var(--muted-foreground))',
}

interface Leaf {
  device: string
  port: MikrotikPort
  x: number
}

// Device-box centers. Leaves are grouped per parent device (each device's
// leaves fan out in its own horizontal band under it) so edges never cross
// between devices.
const devX: Record<string, number> = { e50ug: 235, crs309: 665 }

function deviceTone(d: MikrotikDevice | undefined, latest: MikrotikLatest): string {
  if (!d || !d.reachable) return STROKE.crit
  const tones = d.ports.map((p) => portTempTone(p.sfp_temp_c, latest.temp_thresholds))
  if (tones.includes('crit')) return STROKE.crit
  if (tones.includes('warn')) return STROKE.warn
  return STROKE.up
}

/** True when a port's discovered neighbor identifies `other` (by device id or board name). */
function isInterlinkNeighbor(neighborName: string | null | undefined, other: MikrotikDevice): boolean {
  return !!neighborName && (neighborName === other.device || neighborName === other.board_name)
}

/** The port on `d` (if any) whose neighbor is `other` — i.e. the physical interlink port. */
function findInterlinkPort(
  d: MikrotikDevice | undefined,
  other: MikrotikDevice | undefined,
): MikrotikPort | undefined {
  if (!d || !other) return undefined
  return d.ports.find((p) => isInterlinkNeighbor(p.neighbor?.name, other))
}

/** Color/label for the chain edge between two directly-linked devices, from real interlink port state. */
function interlinkEdge(
  a: MikrotikDevice | undefined,
  b: MikrotikDevice | undefined,
  thresholds: MikrotikLatest['temp_thresholds'],
): { stroke: string; dashed: boolean; label?: string } {
  if (!a?.reachable && !b?.reachable) return { stroke: STROKE.down, dashed: true }

  const ports = [findInterlinkPort(a, b), findInterlinkPort(b, a)].filter(
    (p): p is MikrotikPort => !!p,
  )
  if (ports.length === 0 || ports.some((p) => !p.link)) {
    return { stroke: STROKE.down, dashed: true }
  }

  const tones = ports.map((p) => portTempTone(p.sfp_temp_c, thresholds))
  const stroke = tones.includes('crit') ? STROKE.crit : tones.includes('warn') ? STROKE.warn : STROKE.up
  const labelPort = ports.find((p) => {
    const t = portTempTone(p.sfp_temp_c, thresholds)
    return t === 'crit' || t === 'warn'
  }) ?? ports[0]
  const label = [
    labelPort.speed,
    labelPort.sfp_temp_c != null ? `${labelPort.sfp_temp_c.toFixed(0)}°C` : null,
  ].filter(Boolean).join(' · ')
  return { stroke, dashed: false, label: label || undefined }
}

function Box({
  x, y, w, label, sub, stroke, onClick,
}: {
  x: number; y: number; w: number; label: string
  sub?: string; stroke: string; onClick?: () => void
}) {
  return (
    <g onClick={onClick} className={onClick ? 'cursor-pointer' : undefined}>
      <rect x={x} y={y} width={w} height={sub ? 40 : 30} rx={6}
        fill="hsl(var(--card))" stroke={stroke} strokeWidth={1.5} />
      <text x={x + w / 2} y={y + 18} textAnchor="middle" fontSize={12} fontWeight={600}
        fill="hsl(var(--foreground))">{label}</text>
      {sub && (
        <text x={x + w / 2} y={y + 32} textAnchor="middle" fontSize={9}
          fill="hsl(var(--muted-foreground))">{sub}</text>
      )}
    </g>
  )
}

export function TopologyBand({
  data,
  freebox,
  onSelectDevice,
}: {
  data: MikrotikLatest
  freebox: FreeboxBox | undefined
  onSelectDevice: (name: string) => void
}) {
  const e50ug = data.devices.find((d) => d.device === 'e50ug')
  const crs = data.devices.find((d) => d.device === 'crs309')

  const leaves: Leaf[] = useMemo(() => {
    const byDevice = new Map<string, MikrotikPort[]>()
    for (const d of data.devices) {
      for (const p of d.ports) {
        // uplink between the two devices is drawn in the chain, not as a leaf
        const isInterlink = data.devices.some(
          (o) => o.device !== d.device && isInterlinkNeighbor(p.neighbor?.name, o),
        )
        // the router's WAN port points upstream — already drawn in the chain
        const isWan = p.port.toLowerCase().includes('wan')
        if ((p.link || p.neighbor) && !isInterlink && !isWan) {
          const list = byDevice.get(d.device) ?? []
          list.push(p)
          byDevice.set(d.device, list)
        }
      }
    }
    const out: Leaf[] = []
    for (const [device, ports] of byDevice) {
      ports.sort((a, b) => a.port.localeCompare(b.port, undefined, { numeric: true }))
      const cx = devX[device] ?? 450
      const band = Math.min(ports.length * 108, 430)
      const step = band / ports.length
      ports.forEach((p, i) => {
        const x = cx - band / 2 + step * (i + 0.5)
        out.push({ device, port: p, x: Math.max(56, Math.min(844, x)) })
      })
    }
    return out
  }, [data])

  const freeboxUplink = freebox
    ? (freebox.wan_state === 'up' ? STROKE.up : STROKE.crit)
    : STROKE.down
  const backbone = interlinkEdge(e50ug, crs, data.temp_thresholds)

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox="0 0 900 300" className="min-w-[700px] w-full">
        {/* chain edges */}
        <line x1={120} y1={35} x2={190} y2={35} stroke={freeboxUplink} strokeWidth={2}
          strokeDasharray={freebox ? undefined : '5 4'} />
        <line x1={310} y1={35} x2={380} y2={35} stroke={STROKE.down} strokeWidth={2} strokeDasharray="5 4" />
        <line x1={400} y1={60} x2={devX.e50ug} y2={100} stroke={STROKE.up} strokeWidth={2} />
        <line x1={devX.e50ug + 80} y1={120} x2={devX.crs309 - 80} y2={120} stroke={backbone.stroke} strokeWidth={2}
          strokeDasharray={backbone.dashed ? '5 4' : undefined} />
        {backbone.label && (
          <text x={450} y={114} fontSize={9} textAnchor="middle" fill={backbone.stroke}>{backbone.label}</text>
        )}
        {/* upstream row */}
        <Box x={30} y={20} w={90} label="Freebox"
          sub={freebox ? (freebox.wan_state === 'up' ? 'WAN up' : `WAN ${freebox.wan_state ?? '?'}`) : 'no data'}
          stroke={!freebox ? STROKE.down : freebox.wan_state === 'up' ? STROKE.up : STROKE.crit} />
        <Box x={190} y={20} w={120} label="US-8-60W" sub="non instrumenté" stroke={STROKE.down} />
        <Box x={380} y={20} w={120} label="GS108PE" sub="non instrumenté" stroke={STROKE.down} />
        {/* devices row */}
        <Box x={devX.e50ug - 80} y={100} w={160} label="hEX E50UG"
          sub={e50ug?.reachable
            ? `CPU ${e50ug.cpu_pct?.toFixed(0) ?? '—'}% · ${e50ug.version ?? ''}`
            : 'injoignable'}
          stroke={deviceTone(e50ug, data)}
          onClick={() => onSelectDevice('e50ug')} />
        <Box x={devX.crs309 - 80} y={100} w={160} label="CRS309 (backbone)"
          sub={crs?.reachable
            ? `carte ${crs.temp_board_c?.toFixed(0) ?? '—'}°C · ${crs.version ?? ''}`
            : 'injoignable'}
          stroke={deviceTone(crs, data)}
          onClick={() => onSelectDevice('crs309')} />
        {/* leaves — speed/temp live inside the box: floating edge labels overlap */}
        {leaves.map((l) => {
          const tone = l.port.link
            ? portTempTone(l.port.sfp_temp_c, data.temp_thresholds)
            : 'down'
          const stroke =
            tone === 'crit' ? STROKE.crit : tone === 'warn' ? STROKE.warn
            : tone === 'down' ? STROKE.down : STROKE.up
          const x0 = devX[l.device] ?? 450
          const sub = [
            portLabel(l.port.port),
            l.port.speed?.replace(/bps$/, ''),
            l.port.sfp_temp_c != null ? `${l.port.sfp_temp_c.toFixed(0)}°C` : null,
          ].filter(Boolean).join(' · ')
          return (
            <g key={`${l.device}:${l.port.port}`}>
              <line x1={x0} y1={140} x2={l.x} y2={230} stroke={stroke} strokeWidth={2}
                strokeDasharray={l.port.link ? undefined : '5 4'} />
              <Box x={l.x - 52} y={230} w={104}
                label={l.port.neighbor?.name ?? l.port.neighbor?.ip ?? '?'}
                sub={sub} stroke={stroke} />
            </g>
          )
        })}
      </svg>
    </div>
  )
}
